import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { StatusKey, ArtworkModel, ArtworkRequestOptions } from '@models/types'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['artworks', 'statuses']

type ArtworkFilters = {
  campaign_id?: number,
  order_id?: number,
  status_key?: StatusKey,
}

type UseArtworksOptions = {
  filters?: ArtworkFilters,
  performHttpRequests?: boolean,
  requestOptions?: ArtworkRequestOptions,
}

function useArtworks(options: UseArtworksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
    order_id: filterOrderId,
    status_key: filterStatusKey,
  } = filters

  const {
    updatedEntities: {
      artworks: artworksUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { artworks, orderItems, statuses } = useSelector(reduxState => reduxState.entities)

  const filteredArtworks = useMemo(() => {
    const filtered = Object.values(artworks).filter((artwork: ArtworkModel) => {
      const {
        campaign_id, order_item_id, status_id,
      } = artwork

      const orderItem = order_item_id && orderItems[order_item_id] ? orderItems[order_item_id] : {}
      const status = status_id && statuses[status_id] ? statuses[status_id] : {}

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      // @ts-expect-error TS2339
      const matchOrderId = matchFilterNumber(orderItem.order_id, filterOrderId)
      // @ts-expect-error TS2339
      const matchStatusKey = filterStatusKey ? filterStatusKey === status.key : true

      return matchCampaignId && matchOrderId && matchStatusKey
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [artworksUpdatedAt, statusesUpdatedAt, JSON.stringify(filters)])

  const filteredArtworksCount = filteredArtworks.length
  const hasFilteredArtworks = !!filteredArtworksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredArtworksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingArtworks } = useReduxAction(
    'artworks',
    'loadArtworks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredArtworks,
    filteredArtworksCount,
    hasFilteredArtworks,
    loading: loadingArtworks,
  }
}

export default useArtworks
